
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Target,
  TrendingUp,
  Award,
  Calendar,
  Zap,
  CheckCircle,
  AlertTriangle,
  Clock
} from 'lucide-react';

interface PlayerOverviewProps {
  data: {
    personalBests: {
      maxDistance: number;
      bestAccuracy: number;
      longestSession: number;
      consistencyStreak: number;
    };
    currentForm: 'excellent' | 'good' | 'developing' | 'needs_attention';
    improvementAreas: string[];
    strengths: string[];
    goalProgress: Array<{
      id: string;
      title: string;
      target: number;
      current: number;
      progress: number;
      deadline: string;
      status: 'ahead' | 'on_track' | 'at_risk';
    }>;
  };
}

export function PlayerOverview({ data }: PlayerOverviewProps) {
  const getFormColor = (form: string) => {
    switch (form) {
      case 'excellent': return 'bg-green-500';
      case 'good': return 'bg-blue-500';
      case 'developing': return 'bg-yellow-500';
      case 'needs_attention': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getFormText = (form: string) => {
    switch (form) {
      case 'excellent': return 'Excellent Form';
      case 'good': return 'Good Form';
      case 'developing': return 'Developing';
      case 'needs_attention': return 'Needs Attention';
      default: return 'Unknown';
    }
  };

  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
      {/* Personal Bests */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Max Distance</CardTitle>
          <Target className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{data.personalBests.maxDistance}m</div>
          <p className="text-xs text-muted-foreground">
            Personal best
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Best Accuracy</CardTitle>
          <Award className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{data.personalBests.bestAccuracy}%</div>
          <p className="text-xs text-muted-foreground">
            Peak performance
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Longest Session</CardTitle>
          <Calendar className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{data.personalBests.longestSession}min</div>
          <p className="text-xs text-muted-foreground">
            Endurance record
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Current Form</CardTitle>
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${getFormColor(data.currentForm)}`} />
            <span className="text-sm font-medium">{getFormText(data.currentForm)}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {data.personalBests.consistencyStreak} session streak
          </p>
        </CardContent>
      </Card>

      {/* Strengths */}
      <Card className="md:col-span-1 lg:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-green-600" />
            Strengths
          </CardTitle>
          <CardDescription>
            Current areas of excellence
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {data.strengths.map((strength, index) => (
              <Badge key={index} variant="secondary" className="bg-green-100 text-green-800">
                {strength}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Improvement Areas */}
      <Card className="md:col-span-1 lg:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-orange-600" />
            Focus Areas
          </CardTitle>
          <CardDescription>
            Priority improvement targets
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {data.improvementAreas.map((area, index) => (
              <Badge key={index} variant="outline" className="border-orange-300 text-orange-700">
                {area}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Active Goals Summary */}
      <Card className="md:col-span-2 lg:col-span-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-blue-600" />
            Active Goals Progress
          </CardTitle>
          <CardDescription>
            Current goal status and progress tracking
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            {data.goalProgress.map((goal) => (
              <div key={goal.id} className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-sm">{goal.title}</h4>
                  <div className="flex items-center gap-1">
                    {goal.status === 'ahead' && <CheckCircle className="h-4 w-4 text-green-600" />}
                    {goal.status === 'on_track' && <Clock className="h-4 w-4 text-blue-600" />}
                    {goal.status === 'at_risk' && <AlertTriangle className="h-4 w-4 text-orange-600" />}
                    <span className="text-xs font-medium">
                      {goal.progress.toFixed(0)}%
                    </span>
                  </div>
                </div>
                
                <Progress value={goal.progress} className="h-2" />
                
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{goal.current} / {goal.target}</span>
                  <span>Due: {new Date(goal.deadline).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}